import React, { useEffect, useRef, useState } from 'react';
import FaIcon from '../../../Elements/FaIcon';

const PROMPTS = [
  { title: 'Summarise a report', sub: 'Turn an OPS export into a short brief.' },
  { title: 'Find a planning record', sub: 'Look up an item by ID or evaluator.' },
  { title: 'Draft a client message', sub: 'Compose an on-brand follow-up email.' },
  { title: 'Explain a status', sub: "What does “Client Confirmed” mean here?" },
];

// light inline markdown: **bold** and `code`
function renderText(text) {
  const parts = String(text || '').split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) return <strong key={i}>{p.slice(2, -2)}</strong>;
    if (p.startsWith('`') && p.endsWith('`')) return <code key={i}>{p.slice(1, -1)}</code>;
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

function Message({ m, onCopy, priceShown, onTogglePrice }) {
  const isAI = m.Role === 'assistant';
  return (
    <div className={'ac-msg ' + (isAI ? 'ai' : 'user')}>
      <div className='ac-av'>{isAI ? <span className='mono'>B</span> : <FaIcon iconName='fa-solid fa-user' />}</div>
      <div className='ac-bubblewrap'>
        <div className='ac-who'>
          <span>{isAI ? 'Bravo Assistant' : 'You'}</span>
          {m.Ts ? <span className='ts'>{m.Ts}</span> : null}
        </div>
        <div className='ac-bubble'>
          <p>{renderText(m.Content)}</p>
        </div>
        {isAI ? (
          <div className='ac-tools'>
            <button className='ac-tool' onClick={() => onCopy(m.Content)}>
              <FaIcon iconName='fa-regular fa-copy' /> Copy
            </button>
            {m.FullPrice ? (
              <button
                className={'ac-tool' + (priceShown ? ' on' : '')}
                onClick={() => onTogglePrice(m.MessageId)}
              >
                <FaIcon iconName='fa-solid fa-coins' /> Cost
              </button>
            ) : null}
            {priceShown ? (
              <div className='ac-price' onClick={(e) => e.stopPropagation()}>
                <div>
                  Model: <b>{m.Model || '—'}</b>
                </div>
                <div>
                  Price: <b>${Number(m.FullPrice) || 0}</b>
                </div>
                <div>Prompt: ${Number(m.PromptPrice) || 0}</div>
                <div>Output: ${Number(m.OutputPrice) || 0}</div>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function MessageList({ messages, typing, loading, onCopy, inputRef, onSeedPrompt }) {
  const containerRef = useRef(null);
  const [priceShownId, setPriceShownId] = useState(null);

  const real = (messages || []).filter((m) => m.Role === 'user' || m.Role === 'assistant');
  const system = (messages || []).filter((m) => m.Role === 'system');

  useEffect(() => {
    if (containerRef.current) containerRef.current.scrollTop = containerRef.current.scrollHeight;
    setPriceShownId(null);
  }, [real.length, typing, loading]);

  const seed = (title) => {
    onSeedPrompt(title + ' — ');
    if (inputRef && inputRef.current) inputRef.current.focus();
  };

  const showEmpty = !loading && !typing && real.length === 0 && system.length === 0;

  return (
    <div className='ac-thread' ref={containerRef}>
      {showEmpty ? (
        <div className='ac-empty'>
          <span className='mark'>
            <FaIcon iconName='fa-solid fa-wand-magic-sparkles' />
          </span>
          <h2>How can I help?</h2>
          <p>
            Ask about planning records, evaluators, reports, or anything in your BARE workspace. Your
            conversation stays private to your account.
          </p>
          <div className='ac-prompt-grid'>
            {PROMPTS.map((p) => (
              <button key={p.title} className='ac-prompt-card' onClick={() => seed(p.title)}>
                <b>{p.title}</b>
                <span>{p.sub}</span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className='ac-thread-inner'>
          {loading ? <div className='ac-sysline'>Loading messages…</div> : null}
          {real.map((m) => (
            <Message
              key={m.MessageId}
              m={m}
              onCopy={onCopy}
              priceShown={priceShownId === m.MessageId}
              onTogglePrice={(id) => setPriceShownId((cur) => (cur === id ? null : id))}
            />
          ))}
          {system.map((m) => (
            <div key={m.MessageId} className='ac-sysline'>
              {m.Content}
            </div>
          ))}
          {typing ? (
            <div className='ac-msg ai'>
              <div className='ac-av'>
                <span className='mono'>B</span>
              </div>
              <div className='ac-bubblewrap'>
                <div className='ac-who'>
                  <span>Bravo Assistant</span>
                </div>
                <div className='ac-bubble ac-typing'>
                  <i />
                  <i />
                  <i />
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
